import { IntlMessageFormat } from 'intl-messageformat';
import type { MessageArgs, MessageKey } from './generated/messages';
import { LOCALES, SOURCE_LOCALE, type Direction, type LocaleCode } from './locales';

export type { MessageArgs, MessageKey };
export type Catalog = Partial<Record<MessageKey, string>>;

/**
 * Catalogs arrive through registerCatalog(). The package's main entry registers every language,
 * so apps that import `@kidova/i18n` see no difference; an app that imports `@kidova/i18n/core`
 * registers only the language it shows (a smaller first download). A key missing from a
 * registered language falls back to English when English is registered, else to the key.
 */
export const CATALOGS: Record<LocaleCode, Catalog> = { en: {}, de: {}, ar: {}, tr: {} };

export function registerCatalog(locale: LocaleCode, catalog: Catalog): void {
  Object.assign(CATALOGS[locale], catalog);
}

type ArgsFor<K extends MessageKey> = [MessageArgs[K]] extends [never] ? [] : [args: MessageArgs[K]];

export type TFunction = <K extends MessageKey>(key: K, ...args: ArgsFor<K>) => string;

export interface Translator {
  locale: LocaleCode;
  dir: Direction;
  t: TFunction;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  formatDate: (value: Date | number, options?: Intl.DateTimeFormatOptions) => string;
  /** Localised weekday name; `day` 0 = Monday, matching the policy schema's days_mask bit order. */
  weekday: (day: number, width?: 'long' | 'short' | 'narrow') => string;
}

export interface TranslatorOptions {
  /** Called once per missing key, e.g. to log in development. Defaults to doing nothing. */
  onMissing?: (key: MessageKey, locale: LocaleCode) => void;
}

// 2024-01-01 was a Monday, so adding `day` gives the weekday for any 0 = Monday index.
const MONDAY = Date.UTC(2024, 0, 1);
const DAY_MS = 86_400_000;

export function createTranslator(locale: LocaleCode, options: TranslatorOptions = {}): Translator {
  const info = LOCALES[locale];
  const compiled = new Map<MessageKey, IntlMessageFormat>();
  const reported = new Set<MessageKey>();

  const compile = (key: MessageKey): IntlMessageFormat | undefined => {
    const hit = compiled.get(key);
    if (hit) return hit;
    const own = CATALOGS[locale][key];
    const fallback = CATALOGS[SOURCE_LOCALE][key];
    if (own === undefined && !reported.has(key)) {
      reported.add(key);
      options.onMissing?.(key, locale);
    }
    const message = own ?? fallback;
    if (message === undefined) return undefined;
    // A fallback English message must use English plural rules, whatever the UI locale is.
    const format = new IntlMessageFormat(
      message,
      own === undefined ? LOCALES[SOURCE_LOCALE].intl : info.intl,
    );
    compiled.set(key, format);
    return format;
  };

  const t: TFunction = (key, ...args) => {
    const format = compile(key);
    if (!format) return key;
    return String(format.format(args[0]));
  };

  const weekdayFormats = new Map<string, Intl.DateTimeFormat>();
  return {
    locale,
    dir: info.dir,
    t,
    formatNumber: (value, opts) => new Intl.NumberFormat(info.intl, opts).format(value),
    formatDate: (value, opts) => new Intl.DateTimeFormat(info.intl, opts).format(value),
    weekday: (day, width = 'long') => {
      let format = weekdayFormats.get(width);
      if (!format) {
        format = new Intl.DateTimeFormat(info.intl, { weekday: width, timeZone: 'UTC' });
        weekdayFormats.set(width, format);
      }
      return format.format(MONDAY + day * DAY_MS);
    },
  };
}
